import { from, Observable } from 'rxjs'
import { map } from 'rxjs/operators'

import { GorillaResume } from '@procom-labs/common'

import { environment } from '@resume-portal/environment'
import { axios } from '@resume-portal/lib'
import { FeatureFlags, ResumeFormat } from '@resume-portal/models'

class ResumeParserService {
  private readonly baseUrl = `${environment.GORILLA_RESUME_API_URL}/ResumeParser`

  getParseStatus(taskId: string, orgId: string): Observable<any> {
    return axios.get(`${this.baseUrl}/${taskId}?partitionKey=${orgId}`).pipe(
      map((response) => {
        return response
      })
    )
  }

  parse(
    file: File,
    model: string,
    userEmail: string,
    clientId: string,
    orgId: string,
    featureFlags: FeatureFlags[]
  ): Observable<string> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('model', model)
    formData.append('organizationId', orgId)
    formData.append('partitionKey', orgId)
    formData.append(
      'customFields',
      JSON.stringify({
        userEmail,
        clientId,
        featureFlags,
      })
    )

    return axios
      .post<string>(`${this.baseUrl}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .pipe(
        map(({ data }) => {
          return data
        })
      )
  }

  generateTemplatedResume(
    resume: GorillaResume,
    templateFileName: string,
    organizationId: string,
    fileFormat: ResumeFormat = ResumeFormat.Pdf
  ): Observable<Blob> {
    const payload = {
      parsedResumeData: resume,
      templateFileName,
      organizationId,
      fileFormat,
    }

    return axios
      .post<Blob>(`${this.baseUrl}/generate-templated-resume`, payload, {
        responseType: 'blob',
      })
      .pipe(
        map(({ data }) => {
          return data
        })
      )
  }

  cancelParse(taskId: string, orgId: string): Observable<void> {
    return axios.delete(`${this.baseUrl}/${taskId}/cancel?partitionKey=${orgId}`).pipe(map(() => {}))
  }

  askAi(
    prompt: string,
    token: string
  ): Observable<ReadableStream<Uint8Array> | null> {
    const formData = new FormData()
    formData.append('prompt', prompt)

    return from(
      fetch(`${this.baseUrl}/ask-ai`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => {
          return response.body
        })
        .catch((error) => {
          throw error
        })
    )
  }

  fileToPdf(file: File): Observable<Blob> {
    const formData = new FormData()
    formData.append('file', file, file.name)

    return axios
      .post<Blob>(`${this.baseUrl}/file-to-pdf`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob',
      })
      .pipe(
        map(({ data }) => {
          return data
        })
      )
  }
}

export const resumeParserService = new ResumeParserService()
